import { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import type { UseQueryResult, UseMutationResult } from 'react-query';

import { queryClient } from '@hotjar/webapp/utils/react-query';
import {
  HEATMAP_GENERATION_FAILURE_ERRORS,
  getHeatDataV2,
  getScreenshotDataV2,
  takeScreenshotV2,
  deleteScreenshotV2,
  DEFAULT_POLLING_INTERVAL,
  CACHE_TIME,
  NO_DATA_FOUND_RESPONSE,
  INVALID_SEARCH_RESPONSE,
  SCREENSHOT_DATA_QUERY_KEY,
  TAKE_SCREENSHOT_DATA_QUERY_KEY,
  HEAT_DATA_QUERY_KEY,
} from '@hotjar/heatmaps';

interface useHeatmapDataReturnedValue {
  screenshotData: UseQueryResult<HeatmapAPI.GetScreenshotDataResponse, Error>;
  heatData: UseQueryResult<HeatmapAPI.GetHeatDataResponse<HeatmapDataType>, Error>;
  takeScreenshotData: UseQueryResult<HeatmapAPI.Response, Error>;
  deleteScreenshot: UseMutationResult<
    HeatmapAPI.Response,
    Error,
    HeatmapAPI.DeleteScreenshotV2Request
  >;
}

export interface UseHeatmapDataParams {
  type: HeatmapDataType;
  siteId: SiteId;
  deviceType: HeatmapDeviceTypes;
  url: string;
  matchOperation: TargetingValue;
  jsonQueryString: string;
  retakeParams?: HeatmapAPI.RetakerParams;
  pollingInterval?: number;
}

export const useHeatmapData = ({
  siteId,
  deviceType,
  type,
  url,
  matchOperation,
  jsonQueryString,
  retakeParams,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
}: UseHeatmapDataParams): useHeatmapDataReturnedValue => {
  const [screenshotPollingInterval, setScreenshotPollingInterval] = useState<number | false>(false);

  const screenshotData = useQuery<HeatmapAPI.GetScreenshotDataResponse, Error>(
    [SCREENSHOT_DATA_QUERY_KEY, { siteId, deviceType, url, matchOperation, jsonQueryString }],
    () =>
      getScreenshotDataV2({ siteId, deviceType, url, matchOperation, jsonQueryString }).then(
        (data) => {
          if (data.status === 'failed') {
            throw new Error(HEATMAP_GENERATION_FAILURE_ERRORS.SCREENSHOT_GENERATION_FAILURE);
          }
          return data;
        },
      ),
    {
      retry: false,
      refetchInterval: screenshotPollingInterval,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
      enabled: Boolean(url) && Boolean(siteId),
      onSuccess: (data: HeatmapAPI.GetScreenshotDataResponse) => {
        if (data.status === null || data.status === 'done') {
          setScreenshotPollingInterval(false);
        }
      },
    },
  );

  const takeScreenshotData = useQuery<HeatmapAPI.Response, Error>(
    [
      TAKE_SCREENSHOT_DATA_QUERY_KEY,
      { siteId, deviceType, url, matchOperation, jsonQueryString, ...retakeParams },
    ],
    () =>
      takeScreenshotV2({
        siteId,
        deviceType,
        url,
        matchOperation,
        jsonQueryString,
        ...retakeParams,
      }).catch((err) => {
        switch (err.message) {
          case NO_DATA_FOUND_RESPONSE:
          case INVALID_SEARCH_RESPONSE:
            throw new Error(HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_FOUND);
          default:
            throw new Error(err.message);
        }
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
      enabled: screenshotData.isSuccess && screenshotData?.data?.status === null,
      onSuccess: () => {
        setScreenshotPollingInterval(pollingInterval);
      },
      onError: () => {
        setScreenshotPollingInterval(false);
      },
    },
  );

  const deleteScreenshot = useMutation<
    HeatmapAPI.Response,
    Error,
    HeatmapAPI.DeleteScreenshotV2Request
  >((data: HeatmapAPI.DeleteScreenshotV2Request) => deleteScreenshotV2(data), {
    onSuccess: () => {
      setScreenshotPollingInterval(false);
      queryClient.removeQueries(SCREENSHOT_DATA_QUERY_KEY);
      queryClient.removeQueries(HEAT_DATA_QUERY_KEY);
      queryClient.removeQueries(TAKE_SCREENSHOT_DATA_QUERY_KEY);
    },
  });

  const screenshotId = screenshotData?.data?.screenshotId || 0;

  const heatData = useQuery<HeatmapAPI.GetHeatDataResponse<typeof type>, Error>(
    [
      HEAT_DATA_QUERY_KEY,
      { siteId, deviceType, url, matchOperation, type, jsonQueryString, screenshotId },
    ],
    () =>
      getHeatDataV2({
        siteId,
        type,
        screenshotId,
        url,
        matchOperation,
        jsonQueryString,
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
      enabled: !!screenshotId && screenshotData?.data?.status === 'done',
      onSuccess: () => {
        setScreenshotPollingInterval(false);
      },
    },
  );

  return {
    screenshotData,
    heatData,
    takeScreenshotData,
    deleteScreenshot,
  };
};
